import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { NavItemInterface } from '@app/_interfaces';
import {
    ClearEnvialoData,
    CreateContactList,
    GetAfip960Data,
    GetEnvialoSimpleConfig,
    GetEnvialoSimpleMailList,
    GetFacebookPixelsConfig,
    GetGoogleAnalyticsConfig,
    ManualSync,
    SaveAfip960,
    SetEnvialoSimpleConfig,
    SetFacebookPixelsConfig,
    SetGoogleAnalyticsConfig,
    SetIntegrationsNav,
    SetLoadingFormSubmitIntegrationAction,
    ToogleExecuteSubmit,
} from '@app/pages/tools/integrations/store/integration.actions';
import { TranslateService } from '@ngx-translate/core';
import { ConectionData, MailListItem } from '@app/_models/integrations/envialo-simple.model';
import { McToastService } from '@app/_services';
import { EnvialoSimpleService } from '@app/_services/integrations/envialo_simple.service';
import { catchError, tap } from 'rxjs';
import { AfipService } from '@app/_services/integrations/afip.service';
import { GoogleAnalyticsConfig } from '@app/_models/integrations/google_analytics.model';
import { GoogleAnalyticsService } from '@app/_services/integrations/google_analytics.service';
import { FacebookPixelsConfig } from '@app/_models/integrations/facebook_pixels.model';
import { FacebookPixelsService } from '@app/_services/integrations/facebook_pixels.service';

interface IntegrationStateModel {
    nav: NavItemInterface[];
    executeAction: boolean;
    loadingFormSubmit: boolean;
    envialoSimpleConfig: ConectionData;
    validEnvialoSimpleApiKey: boolean;
    envialoSimpleMailList: MailListItem[];
    manualSyncFormSubmit: boolean;
    afip960Data: string;
    googleAnalyticsData: GoogleAnalyticsConfig;
    facebookPixelsData: FacebookPixelsConfig;
}

@State<IntegrationStateModel>({
    name: 'integration',
    defaults: {
        nav: [],
        executeAction: false,
        loadingFormSubmit: false,
        envialoSimpleConfig: null,
        envialoSimpleMailList: null,
        validEnvialoSimpleApiKey: false,
        manualSyncFormSubmit: false,
        afip960Data: null,
        googleAnalyticsData: null,
        facebookPixelsData: null,
    },
})
@Injectable()
export class IntegrationState {
    constructor(
        private envialoSimpleService: EnvialoSimpleService,
        private toast: McToastService,
        private translate: TranslateService,
        private store: Store,
        private afipService: AfipService,
        private googleAnalyticsService: GoogleAnalyticsService,
        private facebookPixelsService: FacebookPixelsService
    ) {}

    @Selector()
    public static getState(state: IntegrationStateModel) {
        return state;
    }
    @Selector()
    public static getLoadingFormSubmit(state: IntegrationStateModel) {
        return state.loadingFormSubmit;
    }
    @Selector()
    public static nav(state: IntegrationStateModel) {
        return state.nav;
    }

    @Selector()
    public static executeAction(state: IntegrationStateModel) {
        return state.executeAction;
    }

    @Selector()
    public static envialoSimpleConfig(state: IntegrationStateModel) {
        return state.envialoSimpleConfig;
    }

    @Selector()
    public static envialoSimpleMailList(state: IntegrationStateModel) {
        return state.envialoSimpleMailList;
    }

    @Selector()
    public static validEnvialoSimpleApiKey(state: IntegrationStateModel) {
        return state.validEnvialoSimpleApiKey;
    }

    @Selector()
    public static manualSyncFormSubmit(state: IntegrationStateModel) {
        return state.manualSyncFormSubmit;
    }

    @Selector()
    public static afip960Data(state: IntegrationStateModel) {
        return state.afip960Data;
    }

    @Selector()
    public static googleAnalyticsData(state: IntegrationStateModel) {
        return state.googleAnalyticsData;
    }

    @Selector()
    public static facebookPixelsData(state: IntegrationStateModel) {
        return state.facebookPixelsData;
    }

    @Action(SetIntegrationsNav)
    public setNav({ patchState }: StateContext<IntegrationStateModel>) {
        return patchState({
            nav: [
                {
                    label: this.translate.instant('app.nav.custom_code'),
                    link: 'custom-code',
                    icon: 'code',
                    show: true,
                },
            ],
        });
    }

    @Action(ToogleExecuteSubmit)
    public settingsExecuteSubmit(
        { patchState, getState }: StateContext<IntegrationStateModel>,
        { execute }: ToogleExecuteSubmit
    ) {
        patchState({
            executeAction: execute,
        });
    }
    @Action(SetLoadingFormSubmitIntegrationAction)
    public setLoadingFormSubmitIntegration(
        { patchState }: StateContext<IntegrationStateModel>,
        { data }: SetLoadingFormSubmitIntegrationAction
    ) {
        patchState({
            loadingFormSubmit: data,
        });
    }

    @Action(GetEnvialoSimpleConfig)
    public getEnvialoSimpleConfig({ patchState }: StateContext<IntegrationStateModel>) {
        return this.envialoSimpleService.showConection().pipe(
            tap((response) => {
                if (response.status === 'ok' && response.apiKey.length > 0) {
                    patchState({ validEnvialoSimpleApiKey: true });
                    this.store.dispatch(new GetEnvialoSimpleMailList());
                }

                if (response.status === 'error') {
                    patchState({ envialoSimpleMailList: null, validEnvialoSimpleApiKey: false });
                }

                patchState({ envialoSimpleConfig: response });
            }),
            catchError((error) => {
                this.toast.danger(this.translate.instant('app.integrations.envialo_simple.toast.error'));
                throw error;
            })
        );
    }

    @Action(GetEnvialoSimpleMailList)
    public getEnvialoSimpleMailList({ patchState }: StateContext<IntegrationStateModel>) {
        return this.envialoSimpleService.showList().pipe(
            tap((response) => {
                patchState({ envialoSimpleMailList: response });
            }),
            catchError((error) => {
                this.toast.danger(this.translate.instant('app.integrations.envialo_simple.toast.mail_list.error'));
                throw error;
            })
        );
    }

    @Action(CreateContactList)
    public createContactList({ patchState }: StateContext<IntegrationStateModel>, { payload }: CreateContactList) {
        patchState({ loadingFormSubmit: true });
        return this.envialoSimpleService.createList(payload).pipe(
            tap((response) => {
                if (response.data.status === 'ok' && response.data.code === 'msg_contactListCreated') {
                    this.toast.success(
                        this.translate.instant('app.integrations.envialo_simple.dialogs.create_list.toast.success')
                    );
                }

                if (response.data.status === 'error') {
                    switch (response.data.code.name) {
                        case 'is_used':
                            this.toast.warning(
                                this.translate.instant(
                                    'app.integrations.envialo_simple.dialogs.create_list.toast.is_used'
                                )
                            );
                            break;

                        default:
                            this.toast.danger(
                                this.translate.instant(
                                    'app.integrations.envialo_simple.dialogs.create_list.toast.error'
                                )
                            );
                            break;
                    }
                }

                patchState({ loadingFormSubmit: false });
            }),
            catchError((error) => {
                patchState({ loadingFormSubmit: false });
                this.toast.danger(
                    this.translate.instant('app.integrations.envialo_simple.dialogs.create_list.toast.error')
                );
                throw error;
            })
        );
    }

    @Action(SetEnvialoSimpleConfig)
    public setEnvialoSimpleConfig(
        { patchState, getState }: StateContext<IntegrationStateModel>,
        { payload, showToasts }: SetEnvialoSimpleConfig
    ) {
        patchState({ loadingFormSubmit: true });
        return this.envialoSimpleService.updateConection(payload).pipe(
            tap((response) => {
                if (
                    (response?.data?.msg === 'Unauthorized' || response?.data?.msg === 'invalid_key') &&
                    response?.data?.status === 'error'
                ) {
                    if (showToasts) {
                        this.toast.danger(
                            this.translate.instant('app.integrations.envialo_simple.form.toast.invalid_api_key')
                        );
                    }
                    patchState({ validEnvialoSimpleApiKey: false });
                }

                if (response?.data[0]?.status === 'ok') {
                    patchState({ validEnvialoSimpleApiKey: true });
                    if (showToasts) {
                        this.toast.success(
                            this.translate.instant('app.integrations.envialo_simple.form.toast.success')
                        );
                    }
                    const mails = getState().envialoSimpleMailList;
                    if (!mails?.length) {
                        this.store.dispatch(new GetEnvialoSimpleMailList());
                    }
                }

                patchState({ loadingFormSubmit: false });
            }),
            catchError((error) => {
                patchState({ loadingFormSubmit: false });
                if (showToasts) {
                    this.toast.danger(this.translate.instant('app.integrations.envialo_simple.form.toast.error'));
                }
                throw error;
            })
        );
    }

    @Action(ManualSync)
    public manualSync({ patchState }: StateContext<IntegrationStateModel>, { payload }: ManualSync) {
        if (!payload) return;
        patchState({ manualSyncFormSubmit: true });
        return this.envialoSimpleService.manualSync(payload).pipe(
            tap((response) => {
                if (response.data.status === 'ok') {
                    this.toast.success(
                        this.translate.instant('app.integrations.envialo_simple.dialogs.manual_sync.toast.success')
                    );
                }

                patchState({ manualSyncFormSubmit: false });
            }),
            catchError((error) => {
                patchState({ manualSyncFormSubmit: false });
                this.toast.danger(
                    this.translate.instant('app.integrations.envialo_simple.dialogs.manual_sync.toast.error')
                );
                throw error;
            })
        );
    }

    @Action(ClearEnvialoData)
    public ClearEnvialoData({ patchState }: StateContext<IntegrationStateModel>) {
        patchState({
            envialoSimpleMailList: null,
            validEnvialoSimpleApiKey: false,
            envialoSimpleConfig: null,
        });
    }

    @Action(SaveAfip960)
    public SaveAfip960({ patchState }: StateContext<IntegrationStateModel>, { payload }: SaveAfip960) {
        patchState({ loadingFormSubmit: true });
        return this.afipService.save960Form(payload).pipe(
            tap((response) => {
                if (response.data && response.data.length && response.data[0]) {
                    if (payload?.html?.length) {
                        this.toast.success(
                            this.translate.instant('app.integrations.afip.formulario_960.form.toast.success')
                        );
                    } else {
                        this.toast.success(
                            this.translate.instant('app.integrations.afip.formulario_960.form.toast.success_empty')
                        );
                    }
                } else {
                    this.toast.danger(this.translate.instant('app.integrations.afip.formulario_960.form.toast.error'));
                }

                patchState({ loadingFormSubmit: false });
            }),
            catchError((error) => {
                patchState({ loadingFormSubmit: false });
                this.toast.danger(this.translate.instant('app.integrations.afip.formulario_960.form.toast.error'));
                throw error;
            })
        );
    }

    @Action(GetAfip960Data)
    public GetAfip960Data({ patchState }: StateContext<IntegrationStateModel>) {
        return this.afipService.get960FormData().pipe(
            tap((response) => {
                if (response?.success) {
                    patchState({ afip960Data: response?.data[0] });
                } else {
                    this.toast.danger(
                        this.translate.instant('app.integrations.afip.formulario_960.form.toast.error_get_960_data')
                    );
                }
            }),
            catchError((error) => {
                this.toast.danger(
                    this.translate.instant('app.integrations.afip.formulario_960.form.toast.error_get_960_data')
                );
                throw error;
            })
        );
    }

    @Action(GetGoogleAnalyticsConfig)
    public GetGoogleAnalyticsConfig(
        { patchState }: StateContext<IntegrationStateModel>,
        { payload }: GetGoogleAnalyticsConfig
    ) {
        return this.googleAnalyticsService.getConfig(payload).pipe(
            tap((response) => {
                if (response?.success) {
                    patchState({ googleAnalyticsData: response?.data });
                } else {
                    this.toast.danger(this.translate.instant('app.integrations.google_analytics.form.toast.get_error'));
                }
            }),
            catchError((error) => {
                this.toast.danger(this.translate.instant('app.integrations.google_analytics.form.toast.get_error'));
                throw error;
            })
        );
    }

    @Action(SetGoogleAnalyticsConfig)
    public SetGoogleAnalyticsConfig(
        { patchState }: StateContext<IntegrationStateModel>,
        { payload }: SetGoogleAnalyticsConfig
    ) {
        patchState({ loadingFormSubmit: true });
        return this.googleAnalyticsService.setConfig(payload).pipe(
            tap((response) => {
                if (response.success) {
                    this.toast.success(
                        this.translate.instant('app.integrations.google_analytics.form.toast.save_success')
                    );
                } else {
                    this.toast.danger(
                        this.translate.instant('app.integrations.google_analytics.form.toast.save_error')
                    );
                }

                patchState({ loadingFormSubmit: false });
            }),
            catchError((error) => {
                patchState({ loadingFormSubmit: false });
                this.toast.danger(this.translate.instant('app.integrations.google_analytics.form.toast.save_error'));
                throw error;
            })
        );
    }

    @Action(GetFacebookPixelsConfig)
    public GetFacebookPixelsConfig(
        { patchState }: StateContext<IntegrationStateModel>,
        { payload }: GetFacebookPixelsConfig
    ) {
        return this.facebookPixelsService.getConfig(payload).pipe(
            tap((response) => {
                if (response?.success) {
                    patchState({ facebookPixelsData: response?.data });
                } else {
                    this.toast.danger(this.translate.instant('app.integrations.facebook_pixel.form.toast.get_error'));
                }
            }),
            catchError((error) => {
                this.toast.danger(this.translate.instant('app.integrations.facebook_pixel.form.toast.get_error'));
                throw error;
            })
        );
    }

    @Action(SetFacebookPixelsConfig)
    public SetFacebookPixelsConfig(
        { patchState }: StateContext<IntegrationStateModel>,
        { payload }: SetFacebookPixelsConfig
    ) {
        patchState({ loadingFormSubmit: true });
        return this.facebookPixelsService.setConfig(payload).pipe(
            tap((response) => {
                if (response.success) {
                    this.toast.success(
                        this.translate.instant('app.integrations.facebook_pixel.form.toast.save_success')
                    );
                } else {
                    this.toast.danger(this.translate.instant('app.integrations.facebook_pixel.form.toast.save_error'));
                }

                patchState({ loadingFormSubmit: false });
            }),
            catchError((error) => {
                patchState({ loadingFormSubmit: false });
                this.toast.danger(this.translate.instant('app.integrations.facebook_pixel.form.toast.save_error'));
                throw error;
            })
        );
    }
}
